/**
 * CONTENT ITEM TYPE DEFINITIONS
 * 
 * Provides type safety for the ContentArray intermediate format used in
 * the markdown → Y.js conversion pipeline.
 * 
 * Pipeline: Markdown → Tokens → ContentArray → Y.js → ProseMirror
 *                                    ^^^^^^^^
 *                                  These types
 */


/**
 * Formatting range for inline marks (bold, italic, links, etc.)
 * Defines which characters in a text string have which marks applied
 * 
 * @example
 * Text: "This is bold text"
 * Range: { start: 8, end: 12, marks: { strong: true } }
 * Result: "This is **bold** text"
 */
export interface FormattingRange {
  /** Start index (inclusive, 0-based) */
  start: number;
  /** End index (exclusive) */
  end: number;
  /** Marks applied to this range */
  marks: Record<string, any>;
}

/**
 * Base interface for all content items
 */
export interface BaseContentItem {
  /** Node type (must match ProseMirror schema) */
  type: string;
  /** Optional attributes (e.g., heading level, image src) */
  attrs?: Record<string, any>;
  /** Optional metadata (not rendered, used for tracking) */
  metadata?: Record<string, any>;
}

/**
 * Content item with text and optional inline formatting
 * Used for paragraphs, headings, and other text-bearing nodes
 */
export interface TextContentItem extends BaseContentItem {
  /** Text content */
  text: string;
  /** Optional formatting ranges for inline marks */
  formatting?: FormattingRange[];
}

/**
 * Content item with nested child content
 * Used for lists, blockquotes, tables, etc.
 */
export interface NestedContentItem extends BaseContentItem {
  /** Nested content items */
  content: ContentItem[];
}

/**
 * Atomic content item (no text or nested content)
 * Used for horizontal rules, images, etc.
 */
export interface AtomicContentItem extends BaseContentItem {
  // No text or content - just type and attrs
}

/**
 * Union type of all possible content items
 */
export type ContentItem = TextContentItem | NestedContentItem | AtomicContentItem;

/**
 * ContentArray - the intermediate representation between markdown tokens and Y.js
 */
export type ContentArray = ContentItem[];

/**
 * Type guard to check if a value is a valid ContentItem
 */
export function isContentItem(value: unknown): value is ContentItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as any;
  return typeof item.type === 'string';
}

/**
 * Type guard to check if item has text content
 */
export function hasText(item: ContentItem): item is TextContentItem {
  return 'text' in item && typeof (item as any).text === 'string';
}

/**
 * Type guard to check if item has nested content
 */
export function hasContent(item: ContentItem): item is NestedContentItem {
  return 'content' in item && Array.isArray((item as any).content);
}

/**
 * Type guard to check if item has formatting
 */
export function hasFormatting(item: ContentItem): item is TextContentItem {
  return hasText(item) && 'formatting' in item && Array.isArray((item as any).formatting);
}

/**
 * Helper to safely get text from a content item
 */
export function getText(item: ContentItem): string {
  return hasText(item) ? item.text : '';
}

/**
 * Helper to safely get formatting from a content item
 */
export function getFormatting(item: ContentItem): FormattingRange[] {
  return hasFormatting(item) ? item.formatting! : [];
}

/**
 * Helper to safely get nested content from a content item
 */
export function getContent(item: ContentItem): ContentItem[] {
  return hasContent(item) ? item.content : [];
}

